import { Fragment } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { AiCheck } from '@d-orca/shared/ai-answer';
import { CheckIcon } from '@utils/icons';
import styles from './styles.module.css';

interface IAiAnswerReceiptProps {
  /** What the answer checked, in the order the turn first read each source. */
  checks: readonly AiCheck[];
  /** When the answer was written (ISO) — stored, or the `done` frame's arrival. */
  at?: string;
  /** The site's zone. Absent until the working site resolves. */
  tz?: string;
}

/**
 * When, in the SITE's zone, to the minute — with the date when it is not the
 * site's today, so a reopened thread from yesterday saying "5 workers on site"
 * is visibly yesterday's five. Null when there is no time to give: a message
 * written before `at` was returned, or a site whose zone has not resolved — the
 * receipt then names its sources without a time rather than one in the wrong
 * zone (frontend invariant 6: gate, don't default).
 */
function writtenAt(at: string | undefined, tz: string | undefined): string | null {
  if (at === undefined || tz === undefined) return null;
  const written = DateTime.fromISO(at).setZone(tz);
  if (!written.isValid) return null;
  const today = DateTime.now().setZone(tz);
  return written.toFormat(written.hasSame(today, 'day') ? 'HH:mm' : 'M/d HH:mm');
}

/**
 * The answer's receipt: one muted line under it saying what it checked and
 * when — "Checked 10:42 · Schedule · Issues" (answer-surface spec, §1).
 *
 * PROVENANCE, NOT PROCESS. A site manager acts on an answer — forwards it,
 * repeats it in the morning meeting — and an answer they cannot trace is one
 * they cannot repeat with confidence. So each source is named in D-ORCA's own
 * words: never a tool, never the upstream system it came from (frontend
 * invariant 1), never the arguments.
 *
 * A source that failed keeps its place in the warning tone and says so; one read
 * in part says that. The data-gap sentence still stands under the answer — this
 * says WHICH source left the gap. Nothing at all when nothing was read: a reply
 * to "thanks" has no receipt.
 */
export function AiAnswerReceipt({ checks, at, tz }: IAiAnswerReceiptProps) {
  const { t } = useTranslation();
  if (checks.length === 0) return null;
  const time = writtenAt(at, tz);
  return (
    <p className={styles.receipt}>
      <span className={styles.lead}>
        <CheckIcon size={12} />
        {time === null ? t('aiAgent.receipt.checked') : t('aiAgent.receipt.checkedAt', { time })}
      </span>
      {checks.map(({ source, outcome }) => {
        const name = t(`aiAgent.source.${source}`);
        // The separator is text, not a stylesheet `::before`: a screen reader
        // otherwise hears the sources run together into one word.
        return (
          <Fragment key={source}>
            <span className={styles.separator}> · </span>
            <span className={outcome === 'failed' ? styles.failed : undefined}>
              {outcome === 'read' ? name : t(`aiAgent.receipt.${outcome}`, { source: name })}
            </span>
          </Fragment>
        );
      })}
    </p>
  );
}
